import React, { useState } from 'react';
import './PasswordChangeModal.css';

interface PasswordChangeData {
	currentPassword: string;
	newPassword: string;
	confirmPassword: string;
}

interface PasswordChangeModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSave: (passwordData: PasswordChangeData) => Promise<void>;
}

export const PasswordChangeModal: React.FC<PasswordChangeModalProps> = ({ isOpen, onClose, onSave }) => {
	const [passwordData, setPasswordData] = useState<PasswordChangeData>({
		currentPassword: '',
		newPassword: '',
		confirmPassword: '',
	});

	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string>('');
	const [successMessage, setSuccessMessage] = useState<string>('');
	const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

	const handleInputChange = (field: keyof PasswordChangeData, value: string) => {
		setPasswordData((prev) => ({ ...prev, [field]: value }));
		setError('');
		setSuccessMessage('');
		// Clear specific field validation error when user starts typing
		if (validationErrors[field]) {
			setValidationErrors((prev) => ({ ...prev, [field]: '' }));
		}
	};

	const validateForm = (): boolean => {
		const errors: Record<string, string> = {};

		if (!passwordData.currentPassword.trim()) {
			errors.currentPassword = 'Trenutna lozinka je obavezna';
		}

		if (!passwordData.newPassword.trim()) {
			errors.newPassword = 'Nova lozinka je obavezna';
		} else if (passwordData.newPassword.length < 6) {
			errors.newPassword = 'Nova lozinka mora imati najmanje 6 karaktera';
		}

		if (!passwordData.confirmPassword.trim()) {
			errors.confirmPassword = 'Potvrda lozinke je obavezna';
		} else if (passwordData.newPassword !== passwordData.confirmPassword) {
			errors.confirmPassword = 'Lozinke se ne poklapaju';
		}

		if (passwordData.currentPassword && passwordData.newPassword && passwordData.currentPassword === passwordData.newPassword) {
			errors.newPassword = 'Nova lozinka mora biti različita od trenutne';
		}

		setValidationErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validateForm()) {
			return;
		}

		setIsLoading(true);
		setError('');
		setSuccessMessage('');

		try {
			await onSave(passwordData);
			setSuccessMessage('Lozinka je uspešno promenjena');
			setTimeout(() => {
				setSuccessMessage('');
				handleCancel();
			}, 2000);
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Greška pri promeni lozinke');
		} finally {
			setIsLoading(false);
		}
	};

	const handleCancel = () => {
		setPasswordData({
			currentPassword: '',
			newPassword: '',
			confirmPassword: '',
		});
		setError('');
		setSuccessMessage('');
		setValidationErrors({});
		onClose();
	};

	if (!isOpen) return null;

	return (
		<div className='modal-overlay'>
			<div className='modal-content password-change-modal'>
				<div className='modal-header'>
					<h2>Promena lozinke</h2>
					<button className='close-button' onClick={handleCancel} disabled={isLoading}>
						×
					</button>
				</div>

				<form onSubmit={handleSubmit} className='password-change-form'>
					{error && <div className='alert alert-error'>{error}</div>}
					{successMessage && <div className='alert alert-success'>{successMessage}</div>}

					<div className='form-group'>
						<label htmlFor='currentPassword'>Trenutna lozinka *</label>
						<input type='password' id='currentPassword' value={passwordData.currentPassword} onChange={(e) => handleInputChange('currentPassword', e.target.value)} className={validationErrors.currentPassword ? 'error' : ''} placeholder='Unesite trenutnu lozinku' required disabled={isLoading} />
						{validationErrors.currentPassword && <span className='error-text'>{validationErrors.currentPassword}</span>}
					</div>

					<div className='form-group'>
						<label htmlFor='newPassword'>Nova lozinka *</label>
						<input type='password' id='newPassword' value={passwordData.newPassword} onChange={(e) => handleInputChange('newPassword', e.target.value)} className={validationErrors.newPassword ? 'error' : ''} placeholder='Unesite novu lozinku (min. 6 karaktera)' required disabled={isLoading} />
						{validationErrors.newPassword && <span className='error-text'>{validationErrors.newPassword}</span>}
					</div>

					<div className='form-group'>
						<label htmlFor='confirmPassword'>Potvrda nove lozinke *</label>
						<input type='password' id='confirmPassword' value={passwordData.confirmPassword} onChange={(e) => handleInputChange('confirmPassword', e.target.value)} className={validationErrors.confirmPassword ? 'error' : ''} placeholder='Ponovite novu lozinku' required disabled={isLoading} />
						{validationErrors.confirmPassword && <span className='error-text'>{validationErrors.confirmPassword}</span>}
					</div>

					<div className='password-requirements'>
						<p className='requirements-title'>Zahtevi za lozinku:</p>
						<ul className='requirements-list'>
							<li className={passwordData.newPassword.length >= 6 ? 'valid' : ''}>Najmanje 6 karaktera</li>
							<li className={passwordData.newPassword !== passwordData.currentPassword ? 'valid' : ''}>Različita od trenutne lozinke</li>
						</ul>
					</div>

					<div className='form-actions'>
						<button type='button' className='btn btn-secondary' onClick={handleCancel} disabled={isLoading}>
							Otkaži
						</button>
						<button type='submit' className='btn btn-primary' disabled={isLoading}>
							{isLoading ? 'Menjanje...' : 'Promeni lozinku'}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
};
